// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * BaseNode - Pipeline node with connection handles.
 *
 * Simplified: nodes have generic input/output handles without type enforcement.
 * Nodes determine their own input/output flexibility at runtime.
 */

import { memo } from 'react'
import type { ReactNode } from 'react'
import { Handle, Position, useNodeConnections } from '@xyflow/react'
import { Settings, Link2 } from 'lucide-react'
import {
  cn,
  Tooltip,
  DarkTooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import {
  HANDLE_COLOR,
  HANDLE_STYLE,
  NODE_SPACING,
  NODE_WIDTH,
} from '@/subsystems/pipelines'
import { usePipelineNodeContext } from '@/subsystems/pipelines'
import { NodeIcon } from './NodeIcon'
import type { NodeStatus } from '../../types'

/** Handle wrapper that shows filled style when connected */
const ConnectedHandle: React.FC<{
  id: string
  type: 'source' | 'target'
  position: Position
  className?: string
}> = ({ id, type, position, className }) => {
  const connections = useNodeConnections({ handleType: type, handleId: id })
  const isConnected = connections.length > 0

  return (
    <Handle
      id={id}
      type={type}
      position={position}
      data-testid={`pipeline-node-${type === 'source' ? 'output' : 'input'}`}
      className={cn(
        'transition-all duration-150',
        className,
      )}
      style={{
        width: HANDLE_STYLE.size,
        height: HANDLE_STYLE.size,
        background: isConnected ? HANDLE_COLOR : 'white',
        border: `3px solid ${HANDLE_COLOR}`,
        borderRadius: HANDLE_STYLE.borderRadius,
        boxShadow: HANDLE_STYLE.boxShadow,
      }}
    />
  )
}

export interface BaseNodeProps {
  icon: ReactNode
  /** Node type label (e.g., "Text Extraction", "Extraction Analysis") */
  label: string
  /** Whether this node has an input handle */
  hasInput?: boolean
  /** Whether this node has an output handle */
  hasOutput?: boolean
  /**
   * @deprecated Auto-injected via PipelineNodeContext. Node authors don't need to pass this.
   */
  instanceName?: string
  /**
   * @deprecated Auto-injected via PipelineNodeContext. Node authors don't need to pass this.
   */
  configured?: boolean
  /**
   * @deprecated Auto-injected via PipelineNodeContext. Node authors don't need to pass this.
   */
  status?: NodeStatus
  /**
   * @deprecated Auto-injected via PipelineNodeContext. Node authors don't need to pass this.
   */
  selected?: boolean
  /**
   * @deprecated Auto-injected via PipelineNodeContext. Node authors don't need to pass this.
   */
  fromApp?: boolean
  /**
   * @deprecated Auto-injected via PipelineNodeContext. Node authors don't need to pass this.
   */
  isExtension?: boolean
}

export const BaseNode = memo(({
  icon,
  label,
  hasInput = false,
  hasOutput = false,
  // Props can override context for edge cases, but typically auto-injected
  instanceName: instanceNameProp,
  configured: configuredProp,
  status: statusProp,
  selected: selectedProp,
  fromApp: fromAppProp,
  isExtension: isExtensionProp,
}: BaseNodeProps) => {
  // Get common props from context (auto-injected by node type wrapper)
  // Props override context for backward compatibility
  const ctx = usePipelineNodeContext()
  const instanceName = instanceNameProp ?? ctx.instanceName
  const configured = configuredProp ?? ctx.configured ?? false
  const status = statusProp ?? ctx.status ?? 'idle'
  const selected = selectedProp ?? ctx.selected ?? false
  const fromApp = fromAppProp ?? ctx.fromApp ?? false
  const isExtension = isExtensionProp ?? ctx.isExtension ?? false

  const needsConfig = !configured && !fromApp // App nodes don't show config warning

  return (
    <div
      className={cn(
        'relative rounded-lg border',
        'shadow-md transition-all duration-150',
        // Blue tint signals inheritance; nodes remain editable via double-click override
        fromApp && 'border-blue-200 bg-blue-50/30',
        // Extension nodes: normal with subtle accent
        isExtension && 'border-emerald-300 bg-white',
        // Default (inline pipeline nodes): normal
        !fromApp && !isExtension && 'border-slate-300 bg-white',
        // Hover for all nodes
        'hover:shadow-lg',
        selected && 'ring-2 ring-blue-500 ring-offset-1',
      )}
      style={{ width: NODE_WIDTH }}
    >
      {/* Input handle on the left */}
      {hasInput && (
        <ConnectedHandle
          id="input"
          type="target"
          position={Position.Left}
          className="!absolute !top-1/2 !-translate-y-1/2 !-left-[7px]"
        />
      )}

      {/* Header with icon, label, config/lock indicator */}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn(NODE_SPACING.content, 'cursor-default')}>
              <div className={cn('flex items-center', NODE_SPACING.gap)}>
                <NodeIcon icon={icon} status={status} />
                <h3 className="flex-1 min-w-0 text-sm font-medium truncate text-slate-800">
                  {label}
                </h3>
                {/* App nodes: inheritance indicator */}
                {fromApp && (
                  <Link2 className="w-4 h-4 text-blue-400 flex-shrink-0" strokeWidth={2} aria-hidden="true" />
                )}
                {/* Editable nodes: config warning if needed */}
                {!fromApp && needsConfig && (
                  <Settings className="w-4 h-4 text-amber-500 flex-shrink-0" strokeWidth={2} />
                )}
              </div>
            </div>
          </TooltipTrigger>
          <DarkTooltipContent>
            {fromApp ? (
              <div className="space-y-1">
                <p>Inherits from app workspace</p>
                <p className="text-xs text-slate-400">Double-click to override</p>
              </div>
            ) : instanceName ? (
              <p>{instanceName}</p>
            ) : (
              <p>Double-click to configure</p>
            )}
          </DarkTooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Output handle on the right */}
      {hasOutput && (
        <ConnectedHandle
          id="output"
          type="source"
          position={Position.Right}
          className="!absolute !top-1/2 !-translate-y-1/2 !-right-[7px]"
        />
      )}
    </div>
  )
})

BaseNode.displayName = 'BaseNode'
